import React from 'react'
import PropTypes from 'prop-types'
import {
    slidesVisibleValuesDesktop,
    slidesVisibleValuesTablet,
    slidesVisibleValuesMobile
} from 'hec/components/ui/carousel'
import {ProductCategoryCarousel} from 'hec/components/product-category-carousel'

export const ProductCarousel = (props) => {
    const {
        productCategory,
        resultCount,
        addedToCartPodId,
        productTileBorderColor,
        showPeek,
        slidesVisible,
        slidePadding,
        ButtonNavigationColor,
        attributes,
        enableHeadline,
        enableBasicView,
        headlineText
    } = props
    const {className, ...restAttributes} = attributes
    const categoryId = productCategory?.options?.category

    return (
        <ProductCategoryCarousel
            categoryId={categoryId}
            resultCount={resultCount}
            addedToCartPodId={addedToCartPodId}
            productTileBorderColor={productTileBorderColor}
            showPeek={showPeek?.onDesktop ?? false}
            showPeekTablet={showPeek?.onTablet ?? false}
            showPeekMobile={showPeek?.onMobile ?? true}
            slidesVisible={slidesVisible?.onDesktop}
            slidesVisibleTablet={slidesVisible?.onTablet}
            slidesVisibleMobile={slidesVisible?.onMobile}
            slidePadding={slidePadding?.onDesktop}
            slidePaddingTablet={slidePadding?.onTablet}
            slidePaddingMobile={slidePadding?.onMobile}
            enableHeadline={enableHeadline}
            enableBasicView={enableBasicView}
            headlineText={headlineText}
            className={className}
            ButtonNavigationColor={ButtonNavigationColor}
            {...restAttributes}
            data-testid="productCarousel-ProductCategoryCarousel"
        />
    )
}

ProductCarousel.propTypes = {
    attributes: PropTypes.object,
    ButtonNavigationColor: PropTypes.string,
    productTileBorderColor: PropTypes.string,
    productCategory: PropTypes.object,
    resultCount: PropTypes.number.isRequired,
    showPeek: PropTypes.bool,
    showPeekMobile: PropTypes.bool,
    slidesVisible: PropTypes.string,
    slidesVisibleMobile: PropTypes.string,
    slidePadding: PropTypes.string,
    slidePaddingMobile: PropTypes.string,
    addedToCartPodId: PropTypes.string.isRequired,
    accountFormLabels: PropTypes.object.isRequired,
    enableHeadline: PropTypes.bool,
    headlineText: PropTypes.string,
    enableBasicView: PropTypes.bool
}

export const ProductCarouselDefinition = {
    component: ProductCarousel,
    name: 'ProductCarousel',
    noWrap: true,
    inputs: [
        {
            name: 'productCategory',
            type: 'SFCommerceCategory',
            required: true
        },
        {
            name: 'enableBasicView',
            type: 'boolean',
            defaultValue: true,
            friendlyName: 'Enable Basic View',
            helperText: 'Enable to use the basic view for product tiles in the carousel.'
        },
        {
            name: 'resultCount',
            type: 'number',
            defaultValue: 8
        },
        {
            name: 'addedToCartPodId',
            type: 'string',
            required: true
        },
        {name: 'productTileBorderColor', type: 'color'},
        {
            name: 'slidesVisible',
            friendlyName: 'Number of Slides Visible',
            type: 'object',
            subFields: [
                {
                    name: 'onDesktop',
                    type: 'string',
                    enum: slidesVisibleValuesDesktop,
                    defaultValue: '1'
                },
                {
                    name: 'onTablet',
                    type: 'string',
                    enum: slidesVisibleValuesTablet,
                    helperText: 'Leave unset to inherit desktop value'
                },
                {
                    name: 'onMobile',
                    type: 'string',
                    enum: slidesVisibleValuesMobile,
                    defaultValue: '2'
                }
            ]
        },
        {
            name: 'showPeek',
            type: 'object',
            friendlyName: 'Show Peek Options',
            subFields: [
                {
                    name: 'onDesktop',
                    type: 'boolean',
                    default: true,
                    helperText: 'Select true to hide peek on desktop'
                },
                {
                    name: 'onTablet',
                    type: 'boolean',
                    default: false,
                    helperText: 'Select true to show peek on tablet devices'
                },
                {
                    name: 'onMobile',
                    type: 'boolean',
                    default: false,
                    helperText: 'Select true to hide peek on mobile devices'
                }
            ]
        },
        {
            name: 'ButtonNavigationColor',
            type: 'color',
            friendlyName: 'Button Navigation Color',
            helperText: 'Color of the button navigation'
        },
        {
            name: 'slidePadding',
            friendlyName: 'Padding Between Slides',
            type: 'object',
            subFields: [
                {
                    name: 'onDesktop',
                    type: 'string',
                    enum: Array.from({length: 7}, (v, i) => i.toString()),
                    defaultValue: '2',
                    helperText: 'Padding between slides'
                },
                {
                    name: 'onTablet',
                    friendlyName: 'Padding Between Slides Tablet',
                    type: 'string',
                    enum: Array.from({length: 7}, (v, i) => i.toString()),
                    helperText:
                        'Padding between slides on tablet. Leave unset to inherit desktop value.'
                },
                {
                    name: 'onMobile',
                    friendlyName: 'Padding Between Slides Mobile',
                    type: 'string',
                    enum: Array.from({length: 7}, (v, i) => i.toString()),
                    defaultValue: '2',
                    helperText: 'Padding between slides on mobile'
                }
            ]
        },
        {
            name: 'enableHeadline',
            type: 'boolean',
            friendlyName: 'Enable Headline',
            defaultValue: false
        },
        {
            name: 'headlineText',
            type: 'string',
            friendlyName: 'Headline Text',
            defaultValue: 'Explore More',
            helperText: 'Text to display as the headline'
        }
    ]
}
